<template>
    <div class="body bank-app">
        <Navbar :show-back-button="true" @back="navigateToDashboard"/>
        
        <div class="section-header">
            <h4 class="search-title">
                <span class="search-icon">🔍</span>
                {{ $t('sales.kalaSearch.title') }}
            </h4>
        </div>
        
        <!-- Search Section -->
        <div class="search-section">
            <div class="search-card">
                <div class="search-header">
                    <p class="search-subtitle">
                        {{ $t('sales.kalaSearch.inputHint') }}
                    </p>
                </div>
                
                <div class="search-input-group">
                    <div class="search-input-container">
                        <input 
                            type="text" 
                            v-model="searchTerm"
                            @keyup.enter="performSearch"
                            :placeholder="$t('sales.kalaSearch.inputPlaceholder')"
                            class="search-input"
                            :disabled="loading"
                        />
                        <span class="input-icon">📦</span>
                        <button 
                            class="clear-search-btn" 
                            @click="clearSearch"
                            v-if="searchTerm"
                            title="پاک کردن"
                        >
                            ✕
                        </button>
                    </div>
                    
                    <button 
                        class="search-btn"
                        @click="performSearch"
                        :disabled="!searchTerm.trim() || loading"
                    >
                        <span v-if="!loading">
                            <span class="btn-icon">🔍</span>
                            {{ $t('sales.kalaSearch.searchButton') }}
                        </span>
                        <span v-else>
                            <span class="loading-spinner-small"></span>
                            {{ $t('sales.kalaSearch.searching') }}
                        </span>
                    </button>
                </div>

                <!-- Search Options -->
                <div class="search-options">
                    <label class="option-label">
                        <input type="checkbox" v-model="searchOptions.inStockOnly" />
                        {{ $t('sales.kalaSearch.inStockOnly') }}
                    </label>
                    <div class="price-range">
                        <span class="price-label">{{ $t('sales.kalaSearch.priceRange') }}</span>
                        <input 
                            type="number" 
                            v-model.number="searchOptions.minPrice" 
                            :placeholder="$t('sales.kalaSearch.minPrice')"
                            class="price-input"
                            min="0"
                        />
                        <span class="price-separator">-</span>
                        <input 
                            type="number" 
                            v-model.number="searchOptions.maxPrice" 
                            :placeholder="$t('sales.kalaSearch.maxPrice')"
                            class="price-input"
                            min="0"
                        />
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Results Section -->
        <div class="results-section" v-if="showResults">
            <div class="results-header">
                <h4 class="results-title">
                    <span class="results-icon">📋</span>
                    {{ $t('sales.kalaSearch.results.title') }}
                    <span class="results-count" :class="{ 'has-results': kalaList.length > 0 }">
                        ({{ kalaList.length }} {{ $t('sales.kalaSearch.results.count') }})
                    </span>
                </h4>
                
                <div class="results-actions">
                    <button 
                        class="refresh-results-btn"
                        @click="performSearch"
                        :disabled="loading"
                        title="بروزرسانی نتایج"
                    >
                        🔄
                    </button>
                    <button 
                        class="clear-results-btn"
                        @click="clearResults"
                        v-if="kalaList.length > 0"
                        title="پاک کردن نتایج"
                    >
                        ✕ {{ $t('sales.kalaSearch.results.clearResults') }}
                    </button>
                </div>
            </div>
            
            <!-- Loading State -->
            <div v-if="loading" class="loading-results">
                <div class="loading-spinner"></div>
                <span>{{ $t('sales.kalaSearch.results.loading') }}</span>
            </div>
            
            <!-- Error State -->
            <div v-else-if="error" class="error-results">
                <div class="error-icon">⚠️</div>
                <h4>{{ $t('sales.kalaSearch.results.errorTitle') }}</h4>
                <p>{{ error }}</p>
                <button @click="performSearch" class="retry-btn">
                    {{ $t('sales.kalaSearch.results.retry') }}
                </button>
            </div>
            
            <!-- No Results State -->
            <div v-else-if="kalaList.length === 0 && searchPerformed" class="no-results">
                <div class="empty-icon">📦</div>
                <h4>{{ $t('sales.kalaSearch.results.notFoundTitle') }}</h4>
                <p>{{ $t('sales.kalaSearch.results.notFoundMessage', { term: searchTerm }) }}</p>
                <p class="suggestion">
                    {{ $t('sales.kalaSearch.results.suggestion') }}
                </p>
            </div>
            
            <!-- Results Grid -->
            <div v-else-if="kalaList.length > 0" class="kala-grid">
                <div 
                    v-for="(kala) in kalaList" 
                    :key="kala.SubCode"
                    class="kala-card"
                >
                    <div class="kala-card-header">
                        <!-- <div class="kala-image">
                            <img 
                                v-if="kala.Image1" 
                                :src="'data:image/jpeg;base64,' + kala.Image1"
                                :alt="kala.Name"
                                class="kala-img"
                            />
                            <div v-else class="kala-placeholder">
                                📦
                            </div>
                        </div> -->
                        <div class="kala-info">
                            <h5 class="kala-name">{{ kala.Name }}</h5>
                            <div class="kala-meta">
                                <span class="kala-code">کد: {{ kala.SubCode || kala.Commodity_Code }}</span>
                                <span class="kala-unit">واحد: {{ kala.Vahed1 || 'عدد' }}</span>
                            </div>
                        </div>
                    </div>
                    
                    <div class="kala-card-body">
                        <div class="kala-details">
                            <div class="detail-row">
                                <span class="detail-label">💰 {{ $t('sales.kalaSearch.kalaCard.price') }}</span>
                                <span class="detail-value price">{{ formatCurrency(kala.Fe1) }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="detail-label">📦 {{ $t('sales.kalaSearch.kalaCard.stock') }}</span>
                                <span class="detail-value" :class="{ 'low-stock': (kala.StockQuantity || 0) < 10 }">
                                    {{ kala.StockQuantity || 0 }} {{ kala.Vahed1 || '' }}
                                </span>
                            </div>
                            <div class="detail-row" v-if="kala.BarCode">
                                <span class="detail-label">📱 {{ $t('sales.kalaSearch.kalaCard.barcode') }}</span>
                                <span class="detail-value">{{ kala.BarCode }}</span>
                            </div>
                            <div class="detail-row" v-if="kala.Commodity_comment">
                                <span class="detail-label">📝 {{ $t('sales.kalaSearch.kalaCard.description') }}</span>
                                <span class="detail-value description">{{ kala.Commodity_comment }}</span>
                            </div>
                        </div>
                        
                        <div class="kala-actions">
                            <button 
                                class="view-details-btn"
                                @click.stop="viewKalaDetails(kala)"
                            >
                                <span class="btn-icon">👁️</span>
                                {{ $t('sales.kalaSearch.kalaCard.viewDetailsKardex') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Depot Card Price Modal -->
        <div v-if="showDepotModal" class="modal-overlay" @click="closeDepotModal">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>📊 {{ selectedKala?.Name || '' }}</h3>
                    <button class="close-btn" @click="closeDepotModal">×</button>
                </div>
                
                <div class="modal-body">
                    <!-- Loading State -->
                    <div v-if="depotLoading" class="modal-loading">
                        <div class="loading-spinner"></div>
                        <span>در حال بارگیری اطلاعات</span>
                    </div>
                    
                    <!-- Error State -->
                    <div v-else-if="depotError" class="modal-error">
                        <div class="error-icon">⚠️</div>
                        <p>{{ depotError }}</p>
                        <button @click="fetchDepotCardPrice" class="retry-btn">
                            {{ $t('sales.kalaSearch.results.retry') }}
                        </button>
                    </div>
                    
                    <!-- Data Display -->
                    <div v-else-if="depotData.length > 0" class="depot-content">
                        <!-- Summary Cards -->
                        <div class="depot-summary">
                            <div class="summary-item">
                                <span class="summary-label">موجودی کل</span>
                                <span class="summary-value">{{ formatNumber(depotData[0]?.sumX || 0) }}</span>
                            </div>
                            <div class="summary-item">
                                <span class="summary-label">واحد</span>
                                <span class="summary-value">{{ depotData[0]?.Vahed || '-' }}</span>
                            </div>
                            <div class="summary-item">
                                <span class="summary-label">تعداد تراکنش‌ها</span>
                                <span class="summary-value">{{ depotData.length }}</span>
                            </div>
                        </div>

                        <!-- Desktop Table View -->
                        <div class="table-container desktop-view">
                            <table class="depot-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>تاریخ</th>
                                        <th>حساب</th>
                                        <th>نام Act</th>
                                        <th>نوع</th>
                                        <th>ورودی</th>
                                        <th>خروجی</th>
                                        <th>مانده</th>
                                        <th>مبلغ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in depotData" :key="item.IDD || index" :class="getTransactionType(item)">
                                        <td class="row-number">{{ index + 1 }}</td>
                                        <td class="date-cell">{{ formatDate(item.Dates) }}</td>
                                        <td class="account-name-cell">{{ item.NameAccount || '-' }}</td>
                                        <td class="act-name-cell">{{ item.NameAct || '-' }}</td>
                                        <td>
                                            <span class="type-badge" :class="getTransactionType(item)">
                                                {{ getTransactionTypeText(item) }}
                                            </span>
                                        </td>
                                        <td class="amount-cell positive">
                                            {{ formatNumber(item.V || 0) }}
                                            <span class="unit-text" v-if="item.Vahed">{{ item.Vahed }}</span>
                                        </td>
                                        <td class="amount-cell negative">
                                            {{ formatNumber(item.S || 0) }}
                                            <span class="unit-text" v-if="item.Vahed">{{ item.Vahed }}</span>
                                        </td>
                                        <td class="amount-cell balance">
                                            {{ formatCurrency(item.Mande_Rial || 0) }}
                                        </td>
                                        <td class="amount-cell total">
                                            {{ formatCurrency(item.Money_End || 0) }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <!-- Mobile Card View -->
                        <div class="mobile-view">
                            <div v-for="(item, index) in depotData" :key="item.IDD || index" class="depot-card" :class="getTransactionType(item)">
                                <div class="card-header-top">
                                    <span class="card-index">#{{ index + 1 }}</span>
                                    <span class="card-value act-name">{{ item.NameAct || '-' }}</span>
                                    <span class="type-badge" :class="getTransactionType(item)">
                                        {{ getTransactionTypeText(item) }}
                                    </span>
                                </div>
                                <div class="card-two-columns">
                                    <!-- Right Column - Details -->
                                    <div class="card-right">
                                        <!-- Line 1: تاریخ + نام Act -->
                                        <div class="card-row">
                                            <span class="card-label">تاریخ</span>
                                            <span class="card-value">{{ formatDate(item.Dates) }}</span>
                                            <span class="card-divider-dot">•</span>
                                            <span class="card-value act-name">{{ item.Invoice_Auto || '-' }}</span>
                                        </div>
                                        
                                        <!-- Line 2: حساب -->
                                        <div class="card-row">
                                            <span class="card-label">حساب</span>
                                            <span class="card-value account-name">{{ item.TitleTaf1 || '-' }}</span>
                                        </div>
                                        
                                        <!-- Line 3: مقدار و فی -->
                                        <div class="card-row">
                                            <span class="card-label">مقدار</span>
                                            <span class="card-value amount-value">
                                                {{ getQuantity(item) }} 
                                                <span class="unit-text">{{ item.Vahed || '' }}</span>
                                            </span>
                                            <span class="card-divider-dot">•</span>
                                            <span class="card-label">فی</span>
                                            <span class="card-value price-value">{{ formatCurrency(getFeValue(item)) }}</span>
                                        </div>
                                    </div>
                                    
                                    <!-- Divider Line -->
                                    <div class="card-divider"></div>
                                    
                                    <!-- Left Column - Amount -->
                                    <div class="card-left">
                                        <div class="card-amount-label">مقدار مانده</div>
                                        <div class="card-amount-value">{{ formatCurrency(item.sumX || 0) }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Empty State -->
                    <div v-else-if="!depotLoading && !depotError" class="modal-empty">
                        <div class="empty-icon">📭</div>
                        <p>داده ای وجود ندارد</p>
                    </div>
                </div>
                
                <div class="modal-footer">
                    <button class="close-modal-btn" @click="closeDepotModal">
                        {{ $t('common.close') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import Navbar from '@/components/Navbar.vue'
import { SalesService } from '@/services/api'

export default {
    name: 'KalaSearchView',
    components: {
        Navbar
    },
    setup() {
        const router = useRouter()
        
        // ============= LOCAL STORAGE KEYS =============
        const STORAGE_KEYS = {
            SEARCH_TERM: 'kalaSearch_term',
            SEARCH_OPTIONS: 'kalaSearch_options',
            SEARCH_RESULTS: 'kalaSearch_results',
            SEARCH_PERFORMED: 'kalaSearch_performed'
        }
        
        // ============= STATE =============
        const searchTerm = ref('')
        const loading = ref(false)
        const error = ref(null)
        const kalaList = ref([])
        const searchPerformed = ref(false)
        const searchOptions = ref({
            inStockOnly: false,
            minPrice: null,
            maxPrice: null
        })
        
        // Depot Modal State
        const showDepotModal = ref(false)
        const selectedKala = ref(null)
        const depotData = ref([])
        const depotLoading = ref(false)
        const depotError = ref(null)
        
        // ============= SAVE FUNCTIONS =============
        const saveSearchState = () => {
            try {
                localStorage.setItem(STORAGE_KEYS.SEARCH_TERM, searchTerm.value || '')
                localStorage.setItem(STORAGE_KEYS.SEARCH_OPTIONS, JSON.stringify(searchOptions.value))
                localStorage.setItem(STORAGE_KEYS.SEARCH_PERFORMED, String(searchPerformed.value))
                
                if (kalaList.value.length > 0) {
                    localStorage.setItem(STORAGE_KEYS.SEARCH_RESULTS, JSON.stringify(kalaList.value))
                } else {
                    localStorage.removeItem(STORAGE_KEYS.SEARCH_RESULTS)
                }
            } catch (e) {
                console.warn('Could not save search state:', e)
            }
        }
        
        // ============= LOAD FUNCTIONS =============
        const loadSearchState = () => {
            try {
                const savedTerm = localStorage.getItem(STORAGE_KEYS.SEARCH_TERM)
                if (savedTerm) {
                    searchTerm.value = savedTerm
                }
                
                const savedOptions = localStorage.getItem(STORAGE_KEYS.SEARCH_OPTIONS)
                if (savedOptions) {
                    try {
                        const parsed = JSON.parse(savedOptions)
                        searchOptions.value = {
                            inStockOnly: parsed.inStockOnly || false,
                            minPrice: parsed.minPrice || null,
                            maxPrice: parsed.maxPrice || null
                        }
                    } catch (e) {
                        console.warn('Could not parse saved options:', e)
                    }
                }
                
                const savedPerformed = localStorage.getItem(STORAGE_KEYS.SEARCH_PERFORMED)
                if (savedPerformed === 'true') {
                    searchPerformed.value = true
                    
                    const savedResults = localStorage.getItem(STORAGE_KEYS.SEARCH_RESULTS)
                    if (savedResults) {
                        try {
                            const parsed = JSON.parse(savedResults)
                            if (Array.isArray(parsed) && parsed.length > 0) {
                                kalaList.value = parsed
                            }
                        } catch (e) {
                            console.warn('Could not parse saved results:', e)
                        }
                    }
                }
            } catch (e) {
                console.warn('Could not load search state:', e)
            }
        }
        
        // ============= CLEAR FUNCTIONS =============
        const clearSearchState = () => {
            try {
                localStorage.removeItem(STORAGE_KEYS.SEARCH_TERM)
                localStorage.removeItem(STORAGE_KEYS.SEARCH_OPTIONS)
                localStorage.removeItem(STORAGE_KEYS.SEARCH_RESULTS)
                localStorage.removeItem(STORAGE_KEYS.SEARCH_PERFORMED)
            } catch (e) {
                console.warn('Could not clear search state:', e)
            }
        }
        
        // ============= WATCHERS FOR AUTO-SAVE =============
        watch(searchTerm, () => {
            saveSearchState()
        })
        
        watch(searchOptions, () => {
            saveSearchState()
        }, { deep: true })
        
        watch(kalaList, () => {
            saveSearchState()
        }, { deep: true })
        
        // ============= COMPUTED =============
        const showResults = computed(() => {
            return searchPerformed.value && !loading.value
        })
        
        // ============= METHODS =============
        const navigateToDashboard = () => {
            router.push('/employer/dashboard')
        }
        
        const performSearch = async () => {
            if (!searchTerm.value.trim()) {
                error.value = 'لطفاً عبارت جستجو را وارد کنید'
                return
            }
            
            if (searchTerm.value.trim().length < 2) {
                error.value = 'لطفاً حداقل ۲ حرف وارد کنید'
                return
            }
            
            loading.value = true
            error.value = null
            searchPerformed.value = true
            
            try {
                console.log('🔍 Searching kala with term:', searchTerm.value)
                
                const searchRequest = {
                    search_query: searchTerm.value.trim(),
                    min_price: searchOptions.value.minPrice || null,
                    max_price: searchOptions.value.maxPrice || null,
                    in_stock_only: searchOptions.value.inStockOnly,
                    page: 1,
                    page_size: 100
                }
                
                const response = await SalesService.searchKala2(searchRequest)
                
                if (response.data.success) {
                    kalaList.value = response.data.children_kala || []
                    console.log(`✅ Found ${kalaList.value.length} kala items`)
                    
                    if (kalaList.value.length === 0) {
                        error.value = response.data.message || 'کالایی یافت نشد'
                    }
                    
                    // Auto-save after search
                    saveSearchState()
                } else {
                    error.value = response.data.message || 'خطا در جستجو'
                    kalaList.value = []
                }
            } catch (err) {
                console.error('❌ Error searching kala:', err)
                error.value = err.response?.data?.detail || err.message || 'خطا در اتصال به سرور'
                kalaList.value = []
            } finally {
                loading.value = false
            }
        }
        
        const clearSearch = () => {
            searchTerm.value = ''
            clearResults()
            clearSearchState()
        }
        
        const clearResults = () => {
            kalaList.value = []
            searchPerformed.value = false
            error.value = null
            clearSearchState()
        }
        
        const viewKalaDetails = (kala) => {
            console.log('👁️ Viewing kala details:', kala.SubCode)
            router.push({
                name: 'KalaDetail',
                params: { id: kala.SubCode },
                query: { 
                    name: kala.Name,
                    kalaName: kala.Name 
                }
            })
        }
        
        const fetchDepotCardPrice = async () => {
            if (!selectedKala.value) return
            
            depotLoading.value = true
            depotError.value = null
            depotData.value = []
            
            try {
                const commodityCode = selectedKala.value.SubCode
                console.log('📊 Fetching depot card price for commodity:', commodityCode)
                
                const response = await SalesService.getDepotCardPrice(commodityCode)
                
                if (response.data.success) {
                    depotData.value = response.data.data || []
                    console.log(`✅ Found ${depotData.value.length} depot records`)
                } else {
                    depotError.value = response.data.message || 'خطا در دریافت اطلاعات کارت انبار'
                }
            } catch (err) {
                console.error('❌ Error fetching depot card price:', err)
                depotError.value = err.response?.data?.detail || err.message || 'خطا در اتصال به سرور'
            } finally {
                depotLoading.value = false
            }
        }
        
        const closeDepotModal = () => {
            showDepotModal.value = false
            selectedKala.value = null
            depotData.value = []
            depotError.value = null
        }
        
        const getTransactionType = (item) => {
            if (item.V > 0 && item.S === 0) return 'entry'
            if (item.S > 0 && item.V === 0) return 'exit'
            if (item.V > 0 && item.S > 0) return 'both'
            return 'unknown'
        }
        
        const getTransactionTypeText = (item) => {
            const types = {
                entry: 'وارده',
                exit: 'صادره',
                both: 'وارده/صادره',
                unknown: '-'
            }
            return types[getTransactionType(item)] || '-'
        }
        
        const formatDate = (dateStr) => {
            if (!dateStr) return '-'
            return dateStr
        }
        
        const formatNumber = (value) => {
            if (value === null || value === undefined) return '۰'
            return new Intl.NumberFormat('fa-IR').format(value)
        }
        
        const formatCurrency = (amount) => {
            if (amount === null || amount === undefined || amount === '') return '۰'
            
            let num = typeof amount === 'string' ? parseFloat(amount) : amount
            
            if (isNaN(num)) return '۰'
            
            num = Math.round(num * 100) / 100
            
            const formatted = new Intl.NumberFormat('fa-IR', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
                useGrouping: true
            }).format(num)
            
            return formatted.replace(/٫/g, '.')
        }
        
        const getQuantity = (item) => {
            if (item.V > 0 && item.S === 0) {
                return formatNumber(item.V)
            } else if (item.S > 0 && item.V === 0) {
                return formatNumber(item.S)
            } else if (item.V > 0 && item.S > 0) {
                return formatNumber(item.V) + '/' + formatNumber(item.S)
            }
            return '۰'
        }
        
        const getFeValue = (item) => {
            if (item.V > 0) {
                return item.Fe_V || 0
            } else if (item.S > 0) {
                return item.Fe_S || 0
            }
            return 0
        }
        
        // ============= LIFECYCLE =============
        onMounted(() => {
            // Load saved search state first
            loadSearchState()
            
            // If there's a search term but no results, perform search automatically
            if (searchTerm.value && kalaList.value.length === 0 && searchPerformed.value) {
                performSearch()
            }
        })
        
        return {
            // State
            searchTerm,
            loading,
            error,
            kalaList,
            searchPerformed,
            searchOptions,
            showDepotModal,
            selectedKala,
            depotData,
            depotLoading,
            depotError,
            
            // Computed
            showResults,
            
            // Methods
            navigateToDashboard,
            performSearch,
            clearSearch,
            clearResults,
            viewKalaDetails,
            fetchDepotCardPrice,
            closeDepotModal,
            getTransactionType,
            getTransactionTypeText,
            formatDate,
            formatNumber,
            formatCurrency,
            getQuantity,
            getFeValue,
            
            // Storage methods (exposed for debugging)
            saveSearchState,
            loadSearchState,
            clearSearchState
        }
    }
}
</script>

<style scoped>
.bank-app {
    min-height: 100vh;
    background: var(--bg);
    padding: 20px;
    direction: rtl;
}

/* Section Header */
.section-header {
    background: var(--card-bg);
    border-radius: 15px;
    padding: 20px;
    margin-bottom: 20px;
    margin-top: 20px;
    box-shadow: var(--shadow-lg);
}

.search-title {
    text-align: center;
    color: var(--text);
    font-size: 24px;
    font-weight: 700;
    margin: 0;
}

.search-icon {
    margin-left: 10px;
}

/* Search Section */
.search-section {
    margin-bottom: 30px;
}

.search-card {
    background: var(--card-bg);
    border-radius: 15px;
    padding: 25px;
    box-shadow: var(--shadow-md);
    border: 1px solid var(--border);
}

.search-header {
    margin-bottom: 25px;
    text-align: center;
}

.search-subtitle {
    color: var(--subtext);
    font-size: 14px;
    margin: 0;
    opacity: 0.8;
}

/* Search Input Group */
.search-input-group {
    display: flex;
    gap: 15px;
    margin-bottom: 20px;
}

@media (max-width: 768px) {
    .search-input-group {
        flex-direction: column;
    }
}

.search-input-container {
    flex: 1;
    position: relative;
}

.search-input {
    width: 100%;
    padding: 15px 50px 15px 45px;
    border: 2px solid var(--border);
    border-radius: 12px;
    font-size: 16px;
    transition: all 0.3s ease;
    background: var(--card-bg);
    color: var(--text);
}

.search-input:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: 0 0 0 3px var(--ring);
}

.search-input::placeholder {
    color: var(--subtext);
}

.input-icon {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 20px;
    color: var(--subtext);
}

.clear-search-btn {
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);
    background: var(--red);
    color: var(--card-bg);
    border: none;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.clear-search-btn:hover {
    background: var(--primary-hover);
    transform: translateY(-50%) scale(1.1);
}

/* Search Button */
.search-btn {
    padding: 10px 30px;
    background: var(--primary);
    color: var(--card-bg);
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 10px;
    white-space: nowrap;
    min-width: 120px;
    justify-content: center;
}

.search-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    background: var(--primary-hover);
    box-shadow: var(--shadow-md);
}

.search-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-icon {
    font-size: 18px;
}

/* Search Options */
.search-options {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    align-items: center;
    padding-top: 20px;
    border-top: 1px solid var(--border);
}

.option-label {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text);
    font-size: 14px;
    cursor: pointer;
}

.price-range {
    display: flex;
    align-items: center;
    gap: 8px;
}

.price-label {
    color: var(--text);
    font-size: 14px;
}

.price-input {
    width: 100px;
    padding: 6px 10px;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    background: var(--card-bg);
    color: var(--text);
}

.price-input:focus {
    outline: none;
    border-color: var(--primary);
}

.price-separator {
    color: var(--subtext);
}

/* Small Spinner */
.loading-spinner-small {
    width: 20px;
    height: 20px;
    border: 3px solid var(--disabled);
    border-top: 3px solid var(--card-bg);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Results Section */
.results-section {
    margin-bottom: 45px;
}

.results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    background: var(--card-bg);
    padding: 15px 20px;
    border-radius: 15px;
    box-shadow: var(--shadow-md);
}

.results-title {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--text);
    font-size: 18px;
    margin: 0;
}

.results-icon {
    font-size: 20px;
    color: var(--primary);
}

.results-count {
    font-size: 14px;
    color: var(--subtext);
}

.results-count.has-results {
    color: var(--primary);
    font-weight: 600;
}

.results-actions {
    display: flex;
    gap: 10px;
}

.refresh-results-btn,
.clear-results-btn {
    padding: 8px 15px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--card-bg);
}

.refresh-results-btn {
    background: var(--primary);
}

.refresh-results-btn:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-2px);
}

.clear-results-btn {
    background: var(--red);
}

.clear-results-btn:hover {
    background: var(--primary-hover);
    transform: translateY(-2px);
}

/* Loading Results */
.loading-results {
    padding: 40px;
    text-align: center;
    background: var(--card-bg);
    border-radius: 15px;
    margin: 20px 0;
}

.loading-spinner {
    width: 50px;
    height: 50px;
    border: 5px solid var(--disabled);
    border-top: 5px solid var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
}

/* Error Results */
.error-results {
    padding: 40px;
    text-align: center;
    background: var(--card-bg);
    border-radius: 15px;
    margin: 20px 0;
}

.error-icon {
    font-size: 48px;
    margin-bottom: 20px;
    color: var(--red);
}

.error-results h4 {
    color: var(--red);
    margin-bottom: 10px;
}

.error-results p {
    color: var(--subtext);
    margin-bottom: 20px;
}

.retry-btn {
    padding: 10px 25px;
    background: var(--primary);
    color: var(--card-bg);
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.retry-btn:hover {
    transform: translateY(-2px);
    background: var(--primary-hover);
    box-shadow: var(--shadow-md);
}

/* No Results */
.no-results {
    padding: 60px 20px;
    text-align: center;
    background: var(--card-bg);
    border-radius: 15px;
    margin: 20px 0;
}

.empty-icon {
    font-size: 60px;
    color: var(--disabled);
    margin-bottom: 20px;
}

.no-results h4 {
    color: var(--text);
    margin-bottom: 10px;
}

.no-results p {
    color: var(--subtext);
    margin-bottom: 10px;
}

.suggestion {
    font-size: 14px;
    color: var(--subtext);
}

/* Kala Grid */
.kala-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
}

@media (max-width: 768px) {
    .kala-grid {
        grid-template-columns: 1fr;
    }
}

/* Kala Card */
.kala-card {
    background: var(--card-bg);
    border-radius: 15px;
    overflow: hidden;
    box-shadow: var(--shadow-md);
    transition: all 0.3s ease;
    border: 2px solid transparent;
}

.kala-card:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);
    border-color: var(--primary);
}

.kala-card-header {
    padding: 20px;
    background: var(--bg);
    display: flex;
    align-items: center;
    gap: 15px;
}

.kala-image {
    width: 80px;
    height: 80px;
    border-radius: 10px;
    overflow: hidden;
    flex-shrink: 0;
    background: var(--card-bg);
    display: flex;
    align-items: center;
    justify-content: center;
}

.kala-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.kala-placeholder {
    font-size: 40px;
}

.kala-info {
    min-width: 0;
}

.kala-name {
    font-size: 18px;
    font-weight: 700;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.kala-meta {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.kala-code,
.kala-unit {
    font-size: 12px;
    color: var(--subtext);
    background: var(--card-bg);
    padding: 2px 10px;
    border-radius: 12px;
}

/* Card Body */
.kala-card-body {
    padding: 20px;
}

.kala-details {
    margin-bottom: 20px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid var(--border);
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-label {
    color: var(--subtext);
    font-size: 14px;
}

.detail-value {
    color: var(--text);
    font-size: 14px;
    font-weight: 600;
}

.detail-value.price {
    color: var(--primary);
}

.detail-value.low-stock {
    color: var(--red);
}

.detail-value.description {
    font-weight: 400;
    font-size: 13px;
    color: var(--subtext);
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Kala Actions */
.kala-actions {
    display: flex;
    gap: 10px;
}

.view-details-btn,
.add-to-cart-btn {
    padding: 8px 15px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    justify-content: center;
}

.view-details-btn {
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--border);
}

.view-details-btn:hover {
    background: var(--hover);
    transform: translateY(-2px);
}

.add-to-cart-btn {
    background: var(--primary);
    color: var(--card-bg);
}

.add-to-cart-btn:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
}

.add-to-cart-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-icon {
    font-size: 16px;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
    backdrop-filter: blur(4px);
}

.modal-content {
    background: var(--card-bg);
    border-radius: 16px;
   
    width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    border: 1px solid var(--border);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    color: var(--heading);
    display: flex;
    align-items: center;
    gap: 10px;
}

.close-btn {
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    color: var(--subtext);
    padding: 0 8px;
    transition: all 0.3s ease;
}

.close-btn:hover {
    color: var(--text);
    transform: rotate(90deg);
}

.modal-body {
    padding: 24px;
    overflow-y: auto;
    flex: 1;
}

/* Modal Loading */
.modal-loading {
    text-align: center;
    padding: 40px 20px;
}

.modal-loading .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid var(--border);
    border-top: 4px solid var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
}

/* Modal Error */
.modal-error {
    text-align: center;
    padding: 40px 20px;
}

.modal-error .error-icon {
    font-size: 48px;
    margin-bottom: 16px;
}

.modal-error p {
    color: var(--subtext);
    margin-bottom: 16px;
}

/* Modal Empty */
.modal-empty {
    text-align: center;
    padding: 60px 20px;
}

.modal-empty .empty-icon {
    font-size: 60px;
    color: var(--disabled);
    margin-bottom: 16px;
}

.modal-empty p {
    color: var(--subtext);
}

/* Depot Summary */
.depot-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.summary-item {
    background: var(--bg);
    padding: 16px;
    border-radius: 12px;
    text-align: center;
    border: 1px solid var(--border);
}

.summary-label {
    display: block;
    font-size: 12px;
    color: var(--subtext);
    margin-bottom: 4px;
}

.summary-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--text);
}

/* Depot Table */
.table-container {
    overflow-x: auto;
}

.depot-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    direction: rtl;
}

.depot-table thead {
    background: var(--bg);
    border-bottom: 2px solid var(--border);
}

.depot-table th {
    padding: 12px 16px;
    text-align: right;
    font-weight: 600;
    color: var(--subtext);
    white-space: nowrap;
}

.depot-table td {
    padding: 10px 16px;
    border-bottom: 1px solid var(--border);
    color: var(--text);
}

.depot-table tbody tr:hover {
    background: var(--hover);
}

.amount-cell {
    font-variant-numeric: tabular-nums;
    direction: ltr;
    text-align: right;
}

.amount-cell.positive {
    color: var(--green);
}

.amount-cell.negative {
    color: var(--red);
}

.type-badge {
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
}

.type-badge.entry {
    background: #ecfdf5;
    color: #059669;
}

.type-badge.exit {
    background: #fef2f2;
    color: #dc2626;
}

.type-badge.both {
    background: #fffbeb;
    color: #d97706;
}

.type-badge.unknown {
    background: var(--bg);
    color: var(--subtext);
}

/* Modal Footer */
.modal-footer {
    padding: 16px 24px;
    border-top: 1px solid var(--border);
    display: flex;
    justify-content: flex-end;
    flex-shrink: 0;
}

.close-modal-btn {
    padding: 8px 24px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    color: var(--text);
    transition: all 0.3s ease;
}

.close-modal-btn:hover {
    background: var(--hover);
}

/* Responsive */
@media (max-width: 768px) {
    .modal-content {
        max-width: 100vw;
        max-height: 95vh;
        border-radius: 12px;
    }
    
    .modal-header {
        padding: 16px 20px;
    }
    
    .modal-body {
        padding: 16px;
    }
    
    .depot-table {
        font-size: 12px;
    }
    
    .depot-table th,
    .depot-table td {
        padding: 8px 10px;
    }
    
    .depot-summary {
        grid-template-columns: repeat(3, 1fr);
        gap: 10px;
    }
    
    .summary-item {
        padding: 12px;
    }
    
    .summary-value {
        font-size: 16px;
    }
}

@media (max-width: 480px) {
    .depot-summary {
        grid-template-columns: 1fr 1fr;
    }
}

/* Dark mode support */
[data-theme="dark"] .modal-content {
    background: #1a1a2e;
    border-color: #2d2d44;
}

[data-theme="dark"] .depot-table thead {
    background: #1f1f3a;
}

[data-theme="dark"] .depot-table tbody tr:hover {
    background: #2d2d44;
}

[data-theme="dark"] .type-badge.entry {
    background: #064e3b;
    color: #34d399;
}

[data-theme="dark"] .type-badge.exit {
    background: #7f1d1d;
    color: #fca5a5;
}

[data-theme="dark"] .type-badge.both {
    background: #78350f;
    color: #fbbf24;
}

/* Depot Table - Desktop */
.table-container {
    overflow-x: auto;
    margin-bottom: 16px;
}

.depot-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    direction: rtl;
    min-width: 800px;
}

.depot-table thead {
    background: var(--bg);
    border-bottom: 2px solid var(--border);
}

.depot-table th {
    padding: 10px 12px;
    text-align: right;
    font-weight: 600;
    color: var(--subtext);
    white-space: nowrap;
    font-size: 12px;
}

.depot-table td {
    padding: 8px 12px;
    border-bottom: 1px solid var(--border);
    color: var(--text);
    font-size: 13px;
}

.depot-table tbody tr:hover {
    background: var(--hover);
}

.amount-cell {
    font-variant-numeric: tabular-nums;
    direction: ltr;
    text-align: right;
}

.amount-cell.positive {
    color: var(--green);
}

.amount-cell.negative {
    color: var(--red);
}

.type-badge {
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    display: inline-block;
}

.type-badge.entry {
    background: #ecfdf5;
    color: #059669;
}

.type-badge.exit {
    background: #fef2f2;
    color: #dc2626;
}

.type-badge.both {
    background: #fffbeb;
    color: #d97706;
}

.type-badge.unknown {
    background: var(--bg);
    color: var(--subtext);
}

/* Mobile Card View */
.mobile-view {
    display: none;
    flex-direction: column;
    gap: 12px;
}

.depot-card {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid var(--border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border);
    margin-bottom: 10px;
    flex-wrap: wrap;
}

.card-index {
    font-weight: 700;
    color: var(--primary);
    font-size: 14px;
}

.card-date {
    font-size: 13px;
    color: var(--text);
    flex: 1;
}

.card-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.card-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
}

.card-label {
    font-size: 13px;
    color: var(--subtext);
    font-weight: 500;
}

.card-value {
    font-size: 14px;
    color: var(--text);
    font-weight: 600;
}

.card-value.positive {
    color: var(--green);
}

.card-value.negative {
    color: var(--red);
}

/* Responsive Breakpoints */
@media (max-width: 992px) {
    .depot-table {
        font-size: 12px;
        min-width: 700px;
    }
    
    .depot-table th,
    .depot-table td {
        padding: 8px 10px;
    }
}

@media (max-width: 768px) {
    /* Hide table on mobile */
    .desktop-view {
        display: none;
    }
    
    /* Show card view on mobile */
    .mobile-view {
        display: flex;
    }
    
    .depot-summary {
        grid-template-columns: repeat(3, 1fr);
        gap: 10px;
    }
    
    .summary-item {
        padding: 12px;
    }
    
    .summary-value {
        font-size: 16px;
    }
}

@media (max-width: 480px) {
    .depot-summary {
        grid-template-columns: 1fr 1fr;
        gap: 8px;
    }
    
    .depot-card {
        padding: 12px;
    }
    
    .card-header {
        gap: 6px;
    }
    
    .card-row {
        padding: 3px 0;
    }
    
    .card-label {
        font-size: 12px;
    }
    
    .card-value {
        font-size: 13px;
    }
}

/* Dark mode support */
[data-theme="dark"] .depot-card {
    background: #1a1a2e;
    border-color: #2d2d44;
}

[data-theme="dark"] .depot-table tbody tr:hover {
    background: #2d2d44;
}

[data-theme="dark"] .type-badge.entry {
    background: #064e3b;
    color: #34d399;
}

[data-theme="dark"] .type-badge.exit {
    background: #7f1d1d;
    color: #fca5a5;
}

[data-theme="dark"] .type-badge.both {
    background: #78350f;
    color: #fbbf24;
}
/* Mobile Card View */
.mobile-view {
    display: none;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    overflow: hidden;
}

.depot-card {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 12px;
    border: 1px solid var(--border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;
}

.card-two-columns {
    display: flex;
    align-items: stretch;
    gap: 0;
    min-height: 100px;
    width: 100%;
    box-sizing: border-box;
}

/* Left Column - Amount */
.card-left {
    flex: 0 0 30%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10px 6px;
    background: linear-gradient(135deg, var(--primary-light, #eef2ff), var(--bg));
    border-radius: 8px 0 0 8px;
    box-sizing: border-box;
    overflow: hidden;
}

.card-amount-label {
    font-size: 11px;
    color: var(--subtext);
    font-weight: 500;
    margin-bottom: 4px;
    text-align: center;
    white-space: nowrap;
}

.card-amount-value {
    font-size: 16px;
    font-weight: 700;
    color: var(--primary);
    word-break: break-all;
    text-align: center;
    direction: ltr;
    max-width: 100%;
    overflow-wrap: break-word;
    line-height: 1.2;
}

/* Divider */
.card-divider {
    width: 2px;
    background: var(--border);
    flex-shrink: 0;
    margin: 6px 0;
}

/* Right Column - Details */
.card-right {
    flex: 1;
    min-width: 0;
    padding: 6px 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;
    overflow: hidden;
    box-sizing: border-box;
}

.card-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1px 0;
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
}

.card-label {
    font-size: 11px;
    color: var(--subtext);
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;
    min-width: 45px;
}

.card-value {
    font-size: 12px;
    color: var(--text);
    font-weight: 600;
    text-align: left;
    direction: ltr;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 55%;
    flex-shrink: 1;
}

/* For very long text, allow wrapping on smaller screens */
@media (max-width: 480px) {
    .card-value {
        white-space: normal;
        word-break: break-word;
        max-width: 60%;
        font-size: 11px;
        line-height: 1.3;
    }
}

.card-value.positive {
    color: var(--green);
}

.card-value.negative {
    color: var(--red);
}

/* Responsive */
@media (max-width: 768px) {
    .desktop-view {
        display: none;
    }
    
    .mobile-view {
        display: flex;
    }
    
    .card-left {
        flex: 0 0 28%;
        padding: 8px 4px;
    }
    
    .card-amount-value {
        font-size: 14px;
    }
    
    .card-right {
        padding: 4px 8px;
        gap: 2px;
    }
    
    .card-label {
        font-size: 10px;
        min-width: 38px;
    }
    
    .card-value {
        font-size: 11px;
        max-width: 50%;
    }
}

@media (max-width: 480px) {
    .depot-card {
        padding: 10px;
    }
    
    .card-two-columns {
        min-height: 90px;
    }
    
    .card-left {
        flex: 0 0 25%;
        padding: 6px 3px;
    }
    
    .card-amount-label {
        font-size: 9px;
    }
    
    .card-amount-value {
        font-size: 12px;
    }
    
    .card-right {
        padding: 4px 6px;
        gap: 2px;
    }
    
    .card-row {
        gap: 4px;
        padding: 0;
    }
    
    .card-label {
        font-size: 9px;
        min-width: 30px;
    }
    
    .card-value {
        font-size: 10px;
        max-width: 55%;
        white-space: normal;
        word-break: break-word;
        line-height: 1.2;
    }
    
    .card-divider {
        margin: 4px 0;
    }
}

/* Dark mode support */
[data-theme="dark"] .card-left {
    background: linear-gradient(135deg, #1a1a3e, #1a1a2e);
}

[data-theme="dark"] .depot-card {
    background: #1a1a2e;
    border-color: #2d2d44;
}

/* Very small screens */
@media (max-width: 360px) {
    .card-left {
        flex: 0 0 22%;
    }
    
    .card-amount-value {
        font-size: 10px;
    }
    
    .card-label {
        font-size: 8px;
        min-width: 25px;
    }
    
    .card-value {
        font-size: 9px;
        max-width: 50%;
    }
}
/* Mobile Card View */
.mobile-view {
    display: none;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    overflow: hidden;
}

.depot-card {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 10px 12px 12px 12px;
    border: 1px solid var(--border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;
    transition: all 0.3s ease;
}

/* Color coding based on transaction type */
.depot-card.entry {
    border-right: 4px solid #22c55e;
    background: linear-gradient(to left, var(--card-bg), rgba(34, 197, 94, 0.05));
}

.depot-card.exit {
    border-right: 4px solid #ef4444;
    background: linear-gradient(to left, var(--card-bg), rgba(239, 68, 68, 0.05));
}

.depot-card.both {
    border-right: 4px solid #f59e0b;
    background: linear-gradient(to left, var(--card-bg), rgba(245, 158, 11, 0.05));
}

.depot-card.unknown {
    border-right: 4px solid var(--border);
}

/* Card Header Top */
.card-header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 6px;
    margin-bottom: 6px;
    border-bottom: 1px solid var(--border);
}

.card-index {
    font-size: 11px;
    font-weight: 600;
    color: var(--subtext);
}

.type-badge {
    padding: 2px 12px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    display: inline-block;
    flex-shrink: 0;
}

.type-badge.entry {
    background: #22c55e;
    color: white;
}

.type-badge.exit {
    background: #ef4444;
    color: white;
}

.type-badge.both {
    background: #f59e0b;
    color: white;
}

.type-badge.unknown {
    background: var(--bg);
    color: var(--subtext);
}

/* Card Two Columns */
.card-two-columns {
    display: flex;
    align-items: stretch;
    gap: 0;
    min-height: 80px;
    width: 100%;
    box-sizing: border-box;
}

/* Right Column - Details */
.card-right {
    flex: 1;
    min-width: 0;
    padding: 4px 8px 4px 4px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;
    overflow: hidden;
    box-sizing: border-box;
}

.card-row {
    display: flex;
    align-items: center;
    padding: 1px 0;
    gap: 4px;
    width: 100%;
    box-sizing: border-box;
    flex-wrap: wrap;
}

.card-label {
    font-size: 11px;
    color: var(--subtext);
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;
    min-width: 28px;
}

.card-value {
    font-size: 12px;
    color: var(--text);
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 1;
    min-width: 0;
}

.card-value.act-name {
    color: var(--primary);
    font-weight: 500;
}

.card-value.account-name {
    font-weight: 500;
    color: var(--text);
    font-size: 11px;
}

.card-value.amount-value {
    font-weight: 700;
    color: var(--text);
    direction: ltr;
}

.card-value.price-value {
    color: var(--text);
    direction: ltr;
}

.card-divider-dot {
    color: var(--subtext);
    font-size: 10px;
    flex-shrink: 0;
    margin: 0 2px;
}

.unit-text {
    font-size: 10px;
    color: var(--subtext);
    font-weight: 400;
    margin-right: 2px;
}

/* Divider */
.card-divider {
    width: 2px;
    background: var(--border);
    flex-shrink: 0;
    margin: 4px 0;
}

/* Left Column - Amount */
.card-left {
    flex: 0 0 26%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 4px;
    background: linear-gradient(135deg, var(--primary-light, #eef2ff), var(--bg));
    border-radius: 0 8px 8px 0;
    box-sizing: border-box;
    overflow: hidden;
}

/* Color variants for left column based on transaction type */
.depot-card.entry .card-left {
    background: linear-gradient(135deg, #dcfce7, #ecfdf5);
}

.depot-card.exit .card-left {
    background: linear-gradient(135deg, #fee2e2, #fef2f2);
}

.depot-card.both .card-left {
    background: linear-gradient(135deg, #fef3c7, #fffbeb);
}

.card-amount-label {
    font-size: 10px;
    color: var(--subtext);
    font-weight: 500;
    margin-bottom: 3px;
    text-align: center;
    white-space: nowrap;
}

.card-amount-value {
    font-size: 16px;
    font-weight: 700;
    color: var(--primary);
    word-break: break-all;
    text-align: center;
    direction: ltr;
    max-width: 100%;
    overflow-wrap: break-word;
    line-height: 1.2;
}

/* Responsive */
@media (max-width: 768px) {
    .desktop-view {
        display: none;
    }
    
    .mobile-view {
        display: flex;
    }
    
    .card-left {
        flex: 0 0 24%;
        padding: 6px 3px;
    }
    
    .card-amount-value {
        font-size: 14px;
    }
    
    .card-right {
        padding: 3px 6px 3px 3px;
    }
}

@media (max-width: 480px) {
    .depot-card {
        padding: 8px 10px 10px 10px;
    }
    
    .card-two-columns {
        min-height: 70px;
    }
    
    .card-left {
        flex: 0 0 22%;
        padding: 4px 2px;
    }
    
    .card-amount-label {
        font-size: 9px;
    }
    
    .card-amount-value {
        font-size: 12px;
    }
    
    .card-right {
        padding: 2px 4px 2px 2px;
        gap: 2px;
    }
    
    .card-label {
        font-size: 9px;
        min-width: 20px;
    }
    
    .card-value {
        font-size: 10px;
    }
    
    .card-value.account-name {
        font-size: 10px;
    }
    
    .card-divider-dot {
        font-size: 8px;
        margin: 0 1px;
    }
    
    .type-badge {
        font-size: 9px;
        padding: 1px 8px;
    }
    
    .unit-text {
        font-size: 9px;
    }
    
    .card-divider {
        margin: 3px 0;
    }
    
    .card-header-top {
        padding-bottom: 4px;
        margin-bottom: 4px;
    }
    
    .card-index {
        font-size: 10px;
    }
}

/* Dark mode support */
[data-theme="dark"] .depot-card {
    background: #1a1a2e;
    border-color: #2d2d44;
}

[data-theme="dark"] .depot-card.entry {
    border-right-color: #22c55e;
    background: linear-gradient(to left, #1a1a2e, rgba(34, 197, 94, 0.08));
}

[data-theme="dark"] .depot-card.exit {
    border-right-color: #ef4444;
    background: linear-gradient(to left, #1a1a2e, rgba(239, 68, 68, 0.08));
}

[data-theme="dark"] .depot-card.both {
    border-right-color: #f59e0b;
    background: linear-gradient(to left, #1a1a2e, rgba(245, 158, 11, 0.08));
}

[data-theme="dark"] .depot-card.entry .card-left {
    background: linear-gradient(135deg, #064e3b, #1a1a2e);
}

[data-theme="dark"] .depot-card.exit .card-left {
    background: linear-gradient(135deg, #7f1d1d, #1a1a2e);
}

[data-theme="dark"] .depot-card.both .card-left {
    background: linear-gradient(135deg, #78350f, #1a1a2e);
}

[data-theme="dark"] .card-left {
    background: linear-gradient(135deg, #1a1a3e, #1a1a2e);
}

[data-theme="dark"] .type-badge.entry {
    background: #22c55e;
    color: white;
}

[data-theme="dark"] .type-badge.exit {
    background: #ef4444;
    color: white;
}

[data-theme="dark"] .type-badge.both {
    background: #f59e0b;
    color: white;
}
/* Desktop Table View - Styled like mobile cards */
.table-container.desktop-view {
    overflow-x: auto;
    margin: 16px 0;
    border-radius: 12px;
    border: 1px solid var(--card-border);
    background: var(--card-bg);
    box-shadow: 0 2px 8px var(--shadow-1);
}

.depot-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 14px;
    min-width: 800px;
}

.depot-table thead {
    background: var(--bg);
    border-bottom: 2px solid var(--card-border);
}

.depot-table thead th {
    padding: 12px 14px;
    text-align: center;
    font-weight: 600;
    color: var(--heading);
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid var(--card-border);
    white-space: nowrap;
}

.depot-table tbody tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid var(--card-border);
}

.depot-table tbody tr:hover {
    background: var(--ring);
}

.depot-table tbody tr:last-child {
    border-bottom: none;
}

.depot-table tbody td {
    padding: 10px 14px;
    text-align: center;
    vertical-align: middle;
    color: var(--text);
    font-size: 13px;
}

/* Row Number */
.depot-table .row-number {
    font-weight: 600;
    color: var(--subtext);
    font-size: 12px;
    width: 40px;
}

/* Date Cell */
.depot-table .date-cell {
    font-weight: 500;
    color: var(--heading);
    font-size: 13px;
    white-space: nowrap;
}

/* Account Name */
.depot-table .account-name-cell {
    font-weight: 500;
    color: var(--heading);
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Act Name */
.depot-table .act-name-cell {
    color: var(--text);
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Type Badge */
.depot-table .type-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    min-width: 50px;
}

.type-badge.income {
    background: rgba(16, 185, 129, 0.15);
    color: #059669;
}

.type-badge.expense {
    background: rgba(249, 115, 22, 0.15);
    color: #ea580c;
}

.type-badge.transfer {
    background: rgba(59, 130, 246, 0.15);
    color: #2563eb;
}

.type-badge.adjustment {
    background: rgba(139, 92, 246, 0.15);
    color: #7c3aed;
}

/* Amount Cells */
.depot-table .amount-cell {
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;
}

.depot-table .amount-cell.positive {
    color: #059669;
}

.depot-table .amount-cell.negative {
    color: #ea580c;
}

.depot-table .amount-cell.balance {
    color: var(--heading);
    font-weight: 700;
}

.depot-table .amount-cell.total {
    color: var(--primary);
    font-weight: 700;
}

/* Unit Text */
.depot-table .unit-text {
    font-size: 11px;
    color: var(--subtext);
    font-weight: 400;
    margin-right: 2px;
}

/* Row hover effect - like mobile cards */
.depot-table tbody tr {
    border-bottom: 1px solid var(--card-border);
}

.depot-table tbody tr:last-child {
    border-bottom: none;
}

/* Color coding rows by transaction type */
.depot-table tbody tr.income {
    border-right: 3px solid #059669;
}

.depot-table tbody tr.expense {
    border-right: 3px solid #ea580c;
}

.depot-table tbody tr.transfer {
    border-right: 3px solid #2563eb;
}

.depot-table tbody tr.adjustment {
    border-right: 3px solid #7c3aed;
}

/* Responsive - Hide table on mobile, show cards */
@media (max-width: 768px) {
    .desktop-view {
        display: none !important;
    }
    
    .mobile-view {
        display: block !important;
    }
}

@media (min-width: 769px) {
    .mobile-view {
        display: none !important;
    }
    
    .desktop-view {
        display: block !important;
    }
}

/* Optional: Scrollbar styling */
.table-container.desktop-view::-webkit-scrollbar {
    height: 6px;
}

.table-container.desktop-view::-webkit-scrollbar-track {
    background: var(--bg);
    border-radius: 4px;
}

.table-container.desktop-view::-webkit-scrollbar-thumb {
    background: var(--muted-2);
    border-radius: 4px;
}

.table-container.desktop-view::-webkit-scrollbar-thumb:hover {
    background: var(--muted);
}
</style>